import React, { useState, useEffect, useContext, useMemo } from "react";
import { DataContext } from "../contexts/DataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileUpload,
  faSpinner,
  faDownload,
  faTrash,
  faSearch,
  faFilter,
  faSortUp,
  faSortDown,
  faSync,
  faExclamationTriangle,
  faCheckCircle,
  faClipboard,
  faCalendarAlt,
  faFileExcel,
  faTimes,
  faEye,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import * as XLSX from "xlsx";
import UploadResultsModal from "./UploadResultsModal";

const ScheduleProgram = () => {
  const { trafficData } = useContext(DataContext);

  // State management
  const [scheduleData, setScheduleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  // Upload results modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // MP or PT

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);

  // Load schedule data on component mount
  useEffect(() => {
    fetchScheduleData();
  }, []);

  // Filter and search data
  useEffect(() => {
    let filtered = [...scheduleData];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(item =>
        Object.values(item.data || {}).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        item.purchaseOrder?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.trafficStatus?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.trafficStatus === statusFilter);
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter(item => item.fileType === typeFilter);
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = getNestedValue(a, sortConfig.key);
        let bValue = getNestedValue(b, sortConfig.key);

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredData(filtered);
  }, [scheduleData, searchTerm, statusFilter, typeFilter, sortConfig]);

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj) || "";
  };

  const fetchScheduleData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/api/schedule-program`);

      // Check if response has the correct structure
      const rawData = response.data.data || response.data || [];

      // Link with traffic data
      const linkedData = rawData.map(scheduleItem => {
        const matchingTraffic = findMatchingTrafficData(scheduleItem.purchaseOrder);
        return {
          ...scheduleItem,
          trafficStatus: matchingTraffic?.header?.detail?.traffic_data?.status || "No Traffic Data",
          status: matchingTraffic?.header?.detail?.traffic_data?.status || "Unknown",
          referencia: matchingTraffic?.header?.reference || "N/A",
          pedimento: matchingTraffic?.header?.pedimento?.pedimento_number || "N/A"
        };
      });

      setScheduleData(linkedData);
    } catch (error) {
      console.error("Error fetching schedule data:", error);
    } finally {
      setLoading(false);
    }
  };

  const findMatchingTrafficData = (purchaseOrder) => {
    if (!trafficData || !purchaseOrder) return null;

    return trafficData.find(traffic =>
      traffic.header?.purchase_order === purchaseOrder
    );
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log("Starting file upload:", file.name);
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus("Initializing upload...");

    const uploadStartTime = Date.now();

    try {
      // Parse Excel file on client-side
      setUploadStatus("Reading Excel file...");
      setUploadProgress(10);
      console.log("Parsing Excel file...");
      const data = await file.arrayBuffer();

      setUploadStatus("Parsing Excel data...");
      setUploadProgress(25);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);

      console.log("Parsed Excel data:", jsonData.length, "rows");
      console.log("Sample row:", jsonData[0]);

      // Determine file type based on columns
      setUploadStatus("Detecting file type...");
      setUploadProgress(35);
      const fileType = determineFileType(jsonData[0] || {});
      console.log("Detected file type:", fileType);

      // Process and upload data
      setUploadStatus("Processing data...");
      setUploadProgress(50);
      const processedData = jsonData.map(row => {
        const purchaseOrder = extractPurchaseOrder(row, fileType);
        return {
          data: row,
          purchaseOrder,
          fileType,
          fileName: file.name,
          uploadDate: new Date().toISOString()
        };
      });

      console.log("Processed data sample:", processedData[0]);

      // Send to backend
      setUploadStatus("Uploading to server...");
      setUploadProgress(70);
      console.log("Sending to backend...");
      const uploadResponse = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/schedule-program/upload`, {
        scheduleData: processedData,
        fileType,
        fileName: file.name,
        fileSize: file.size
      });

      console.log("Upload response:", uploadResponse.data);

      setUploadStatus("Processing server response...");
      setUploadProgress(90);

      // Process upload results for modal
      const uploadResult = {
        timestamp: new Date().toISOString(),
        fileInfo: {
          name: file.name,
          type: fileType,
          size: file.size,
          sizeFormatted: formatFileSize(file.size)
        },
        summary: {
          totalProcessed: uploadResponse.data.totalProcessed || jsonData.length,
          newRecords: uploadResponse.data.summary?.inserted || 0,
          updatedRecords: uploadResponse.data.summary?.updated || 0,
          skippedRecords: uploadResponse.data.summary?.skipped || 0,
          failedRecords: uploadResponse.data.summary?.failed || 0,
          successRate: uploadResponse.data.summary?.successRate || 0
        },
        timing: {
          processingTimeFormatted: uploadResponse.data.timing?.processingTimeFormatted || `${Date.now() - uploadStartTime}ms`,
          recordsPerSecond: uploadResponse.data.timing?.recordsPerSecond || Math.round(jsonData.length / ((Date.now() - uploadStartTime) / 1000))
        },
        statistics: uploadResponse.data.statistics || {},
        dataQuality: uploadResponse.data.dataQuality || null
      };

      // Set upload result and show modal
      setUploadStatus("Upload completed successfully!");
      setUploadProgress(100);
      setUploadResult(uploadResult);
      setShowUploadModal(true);

      // Refresh data
      console.log("Refreshing data...");
      await fetchScheduleData();

      // Clear file input
      event.target.value = "";

    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadStatus("Upload failed");
      setUploadProgress(0);

      // Show error in modal format
      const errorResult = {
        timestamp: new Date().toISOString(),
        fileInfo: {
          name: file.name,
          type: determineFileType({}),
          size: file.size,
          sizeFormatted: formatFileSize(file.size)
        },
        summary: {
          totalProcessed: 0,
          newRecords: 0,
          updatedRecords: 0,
          skippedRecords: 0,
          failedRecords: 0,
          successRate: 0
        },
        timing: {
          processingTimeFormatted: `${Date.now() - uploadStartTime}ms`,
          recordsPerSecond: 0
        },
        error: error.response?.data?.error || error.message,
        statistics: {
          failedRecords: [{
            error: error.response?.data?.error || error.message,
            reason: "Upload failed"
          }]
        }
      };

      setUploadResult(errorResult);
      setShowUploadModal(true);
    } finally {
      setUploading(false);
      // Reset progress after a delay
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStatus("");
      }, 2000);
    }
  };

  const determineFileType = (firstRow) => {
    const keys = Object.keys(firstRow);
    if (keys.includes("PO")) return "MP";
    if (keys.includes("Purch. Order PRD")) return "PT";
    return "Unknown";
  };

  const extractPurchaseOrder = (row, fileType) => {
    if (fileType === "MP") return row["PO"];
    if (fileType === "PT") return row["Purch. Order PRD"];
    return "";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getStatusColorClass = (status) => {
    switch (status) {
      case "Pagado":
        return "bg-green-100 text-green-800";
      case "Open":
        return "bg-yellow-100 text-yellow-800";
      case "No Traffic Data":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const getTransportIcon = (entryPoint) => {
    const transportMap = {
      160: { icon: faShip, color: "text-blue-600" },
      510: { icon: faShip, color: "text-blue-600" },
      430: { icon: faShip, color: "text-blue-600" },
      240: { icon: faTruck, color: "text-green-600" },
      520: { icon: faPlane, color: "text-purple-600" },
      521: { icon: faPlane, color: "text-purple-600" },
      640: { icon: faPlane, color: "text-purple-600" },
    };
    return transportMap[entryPoint] || { icon: faQuestion, color: "text-gray-600" };
  };

  const clearScheduleData = async () => {
    if (window.confirm("Are you sure you want to clear all schedule data?")) {
      try {
        await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/api/schedule-program`);
        setScheduleData([]);
      } catch (error) {
        console.error("Error clearing schedule data:", error);
      }
    }
  };

  const exportToExcel = () => {
    const exportData = filteredData.map(item => ({
      ...item.data,
      "Traffic Status": item.trafficStatus,
      "Status": item.status,
      "File Type": item.fileType,
      "Referencia": item.referencia,
      "Pedimento": item.pedimento,
      "Upload Date": new Date(item.uploadDate).toLocaleDateString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Schedule Program");
    XLSX.writeFile(wb, `schedule_program_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Get unique column names from all data
  const allColumns = useMemo(() => {
    const columns = new Set();
    scheduleData.forEach(item => {
      if (item.data) {
        Object.keys(item.data).forEach(key => columns.add(key));
      }
    });
    return Array.from(columns).sort();
  }, [scheduleData]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Schedule Program</h2>
              <p className="text-sm text-gray-600 mt-1">
                Upload and manage scheduled arrivals (MP and PT files)
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {/* File Upload */}
              <div className="relative">
                <input
                  type="file"
                  id="fileUpload"
                  className="absolute inset-0 opacity-0 w-full cursor-pointer"
                  onChange={handleFileUpload}
                  accept=".xlsx,.xls"
                  disabled={uploading}
                />
                <button
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                  disabled={uploading}
                >
                  <FontAwesomeIcon
                    icon={uploading ? faSpinner : faFileUpload}
                    className={uploading ? "animate-spin" : ""}
                  />
                  <span>{uploading ? "Uploading..." : "Upload Excel"}</span>
                </button>
              </div>

              {/* Upload Progress Bar */}
              {uploading && (
                <div className="flex-1 max-w-xs">
                  <div className="text-xs text-gray-600 mb-1">
                    {uploadStatus} ({uploadProgress}%)
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Export Button */}
              <button
                onClick={exportToExcel}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                disabled={filteredData.length === 0}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Export</span>
              </button>

              {/* Clear Data Button */}
              <button
                onClick={clearScheduleData}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                disabled={scheduleData.length === 0}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span>Clear All</span>
              </button>

              {/* Refresh Button */}
              <button
                onClick={fetchScheduleData}
                className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                disabled={loading}
              >
                <FontAwesomeIcon
                  icon={loading ? faSpinner : faSync}
                  className={loading ? "animate-spin" : ""}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search schedule data..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>Filters</span>
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="Pagado">Paid</option>
                  <option value="Open">Open</option>
                  <option value="No Traffic Data">No Traffic Data</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Type:</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="MP">MP (Materia Prima)</option>
                  <option value="PT">PT (Producto Terminado)</option>
                </select>
              </div>

              <button
                onClick={() => {
                  setStatusFilter("all");
                  setTypeFilter("all");
                  setSearchTerm("");
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Data Summary */}
      <div className="px-6 py-4 bg-white border-b">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {currentItems.length} of {filteredData.length} entries
            {scheduleData.length > 0 && (
              <span className="ml-2">
                (Total uploaded: {scheduleData.length})
              </span>
            )}
          </div>

          {scheduleData.length > 0 && (
            <div className="flex items-center space-x-4 text-sm">
              <span className="flex items-center">
                <span className="w-3 h-3 bg-green-100 rounded-full mr-2"></span>
                Paid: {scheduleData.filter(item => item.trafficStatus === "Pagado").length}
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 bg-yellow-100 rounded-full mr-2"></span>
                Open: {scheduleData.filter(item => item.trafficStatus === "Open").length}
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 bg-gray-100 rounded-full mr-2"></span>
                No Data: {scheduleData.filter(item => item.trafficStatus === "No Traffic Data").length}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-3xl" />
            <span className="ml-3 text-lg">Loading schedule data...</span>
          </div>
        ) : scheduleData.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faFileExcel} className="text-gray-400 text-5xl mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedule Data</h3>
            <p className="text-gray-600 mb-6">Upload an Excel file to get started</p>
            <div className="relative inline-block">
              <input
                type="file"
                id="emptyFileUpload"
                className="absolute inset-0 opacity-0 w-full cursor-pointer"
                onChange={handleFileUpload}
                accept=".xlsx,.xls"
                disabled={uploading}
              />
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium">
                Upload Excel File
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Data Table */}
            <div className="bg-white shadow overflow-hidden rounded-lg">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {/* Purchase Order Column */}
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <button
                          className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                          onClick={() => requestSort("purchaseOrder")}
                        >
                          <span>Purchase Order</span>
                          {sortConfig.key === "purchaseOrder" && (
                            <FontAwesomeIcon
                              icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
                              className="ml-1 text-blue-500"
                              size="xs"
                            />
                          )}
                        </button>
                      </th>

                      {/* Traffic Status Column */}
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <button
                          className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                          onClick={() => requestSort("trafficStatus")}
                        >
                          <span>Traffic Status</span>
                          {sortConfig.key === "trafficStatus" && (
                            <FontAwesomeIcon
                              icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
                              className="ml-1 text-blue-500"
                              size="xs"
                            />
                          )}
                        </button>
                      </th>

                      {/* Status Column */}
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <button
                          className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                          onClick={() => requestSort("status")}
                        >
                          <span>Status</span>
                          {sortConfig.key === "status" && (
                            <FontAwesomeIcon
                              icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
                              className="ml-1 text-blue-500"
                              size="xs"
                            />
                          )}
                        </button>
                      </th>

                      {/* File Type Column */}
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <button
                          className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                          onClick={() => requestSort("fileType")}
                        >
                          <span>Type</span>
                          {sortConfig.key === "fileType" && (
                            <FontAwesomeIcon
                              icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
                              className="ml-1 text-blue-500"
                              size="xs"
                            />
                          )}
                        </button>
                      </th>

                      {/* Referencia Column */}
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <button
                          className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                          onClick={() => requestSort("referencia")}
                        >
                          <span>Referencia</span>
                          {sortConfig.key === "referencia" && (
                            <FontAwesomeIcon
                              icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
                              className="ml-1 text-blue-500"
                              size="xs"
                            />
                          )}
                        </button>
                      </th>

                      {/* Pedimento Column */}
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <button
                          className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                          onClick={() => requestSort("pedimento")}
                        >
                          <span>Pedimento</span>
                          {sortConfig.key === "pedimento" && (
                            <FontAwesomeIcon
                              icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
                              className="ml-1 text-blue-500"
                              size="xs"
                            />
                          )}
                        </button>
                      </th>

                      {/* Dynamic columns from Excel data */}
                      {allColumns.slice(0, 5).map((column) => (
                        <th
                          key={column}
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          <button
                            className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                            onClick={() => requestSort(`data.${column}`)}
                          >
                            <span>{column}</span>
                            {sortConfig.key === `data.${column}` && (
                              <FontAwesomeIcon
                                icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
                                className="ml-1 text-blue-500"
                                size="xs"
                              />
                            )}
                          </button>
                        </th>
                      ))}

                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        {/* Purchase Order */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.purchaseOrder || "N/A"}
                        </td>

                        {/* Traffic Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColorClass(
                              item.trafficStatus
                            )}`}
                          >
                            {item.trafficStatus}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColorClass(
                              item.status
                            )}`}
                          >
                            {item.status}
                          </span>
                        </td>

                        {/* File Type */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.fileType === "MP" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
                          }`}>
                            {item.fileType}
                          </span>
                        </td>

                        {/* Referencia */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.referencia || "N/A"}
                        </td>

                        {/* Pedimento */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.pedimento || "N/A"}
                        </td>

                        {/* Dynamic Excel columns */}
                        {allColumns.slice(0, 5).map((column) => (
                          <td key={column} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.data?.[column] || "N/A"}
                          </td>
                        ))}

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => {
                              // You can implement a detail modal here
                              console.log("View details for:", item);
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, filteredData.length)}
                      </span>{" "}
                      of <span className="font-medium">{filteredData.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Results Modal */}
      <UploadResultsModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        uploadResult={uploadResult}
      />
    </div>
  );
};

export default ScheduleProgram;